import React, { lazy, useEffect, useState } from "react";
import {
  useParams,
  useNavigate,
  Routes,
  Route,
  Link,
  Navigate,
} from "react-router-dom";
import LazyLoading from "../common/components/LazyLoading/LazyLoading";
import { PropertyRouter } from "./PropertyRouter";
import Navbar from "../common/components/Navbar/Navbar";
import AnnouncementBar from "../pages/homepage/components/AnnouncementBar";
import { useSelector } from "react-redux";
import {
  useAllServiesQuery,
  useLazyAllServiesQuery,
} from "../redux/features/servicesQuery";

// Define lazy components outside to prevent re-creation on every render
const ServicesPage = lazy(() =>
  import(
    "../pages/Services/ServicesContainer/components/ServicePage/ServicePage"
  )
);

const ErrorPage = lazy(() => import("../pages/404Error/404Error"));

// These five used to be declared INSIDE the component body, which broke SEO on
// every service and state page. `lazy()` returns a NEW component type each time
// it runs, so re-declaring them per render made React treat them as different
// components: the shared <React.Suspense> below re-suspended, swapped the whole
// subtree for its fallback, and remounted it. That subtree contains the page's
// <SEO/> — and react-helmet-async REMOVES its managed tags on unmount, leaving
// only a stale <title>. The prerenderer saw the result as "title changed, all
// tags gone" on /mortgage-report, /ptin, /property-tax and every other
// top-level service page, and live crawlers saw the same: no description, no
// canonical, no og:*. Keep every lazy() at module scope.
const StatesDocs = lazy(() => import("../pages/state/State"));
const StateDocuments = lazy(() =>
  import("../pages/statedocuments/StateDocuments")
);
const State = lazy(() => import("../pages/StateDoccuments/State"));
// eslint-disable-next-line no-unused-vars
const Property = lazy(() =>
  import(
    "../pages/Services/ServicesContainer/components/SelectProperty/SelecteProperty"
  )
);
const Footer = lazy(() => import("../common/components/Footer/Footer"));

const DynamicServiceResolver = () => {
  const { serviceSlug } = useParams();
  const { data: newData } = useAllServiesQuery();

  const [isValidService, setIsValidService] = useState(null);

  const staticServices = [
    {
      id: "registration-service",
      name: "Registration Service",
    },
    {
      id: "find-ancestor-property",
      name: "Find / Locate Property",
    },
    {
      id: "property-alert-service",
      name: "Property Monitoring & Alerts",
    },
    {
      id: "electricity-name-change",
      name: "Electricity Bill Name Update",
    },
    {
      id: "mutation-creation",
      name: "Mutation Creation",
    },
    {
      id: "vlt-creation",
      name: "VLTIN Creation",
    },
    {
      id: "loan-lap",
      name: "Loan / LAP Help Service",
    },
    {
      id: "property-repatriation",
      name: "Property Repatriation",
    },
    {
      id: "document-translation",
      name: "Document Translation",
    },
    {
      id: "ptin",
      name: "PTIN Creation",
    },
    {
      id: "title-verification-report",
      name: " Legal Opinion /Title Verification",
    },
    {
      id: "court-cases-dispute-check",
      name: "Court-Cases / Dispute Check",
    },
    {
      id: "rectification-deed",
      name: "Rectification Deed",
    },
    {
      id: "property-valuation-service",
      name: "Property Valuation",
    },
    {
      id: "digital-land-survey",
      name: "Digital Land Survey",
    },
    {
      id: "transferable-development-right",
      name: "TDR (Transferable Development Rights) Assistance",
    },
    {
      id: "buy-rent-sell",
      name: "Buy / Sell / Rent Property(End-to-End Assistance)",
    },
    {
      id: "physical-verification",
      name: "Physical Verification",
    },
    {
      id: "physical-protection",
      name: "Physical Protection (Property Watch / Maintenance)",
    },
    {
      id: "property-tax",
      name: "Property Tax",
    },
    {
      id: "electricity-bill",
      name: "Electricity Bill",
    },
    {
      id: "water-bill",
      name: "Water Bill",
    },
    {
      id: "vacant-land-tax",
      name: "Vacant Land Tax",
    },
  ];

  const newstates = [
    {
      id: "telangana",
      name: "Telangana",
      preview: true,
    },
    {
      id: "andhra-pradesh",
      name: "Andhra Pradesh",
      preview: true,
    },
    {
      id: "tamil-nadu",
      name: "Tamil Nadu",
      preview: true,
    },
    {
      id: "maharashtra",
      name: "Maharashtra",
      preview: true,
    },
    {
      id: "karnataka",
      name: "Karnataka",
      preview: true,
    },
    {
      id: "kerala",
      name: "Kerala",
      preview: true,
    },
    {
      id: "odisha",
      name: "Odisha",
      preview: true,
    },
    {
      id: "goa",
      name: "Goa",
      preview: true,
    },
    {
      id: "puducherry",
      name: "Pondicherry",
      preview: true,
    },
    {
      id: "punjab",
      name: "Punjab",
      preview: true,
    },
    {
      id: "delhi",
      name: "Delhi",
      preview: true,
    },
    {
      id: "gujarat",
      name: "Gujarat",
      preview: true,
    },
    {
      id: "west-bengal",
      name: "West Bengal",
      preview: true,
    },
    {
      id: "uttar-pradesh",
      name: "Uttar Pradesh",
      preview: true,
    },
    {
      id: "bihar",
      name: "Bihar",
      preview: true,
    },
    {
      id: "rajasthan",
      name: "Rajasthan",
      preview: true,
    },
    {
      id: "haryana",
      name: "Haryana",
      preview: true,
    },
    {
      id: "uttarakhand",
      name: "Uttarakhand",
      preview: true,
    },
    {
      id: "madhya-pradesh",
      name: "Madhya Pradesh",
      preview: true,
    },
    {
      id: "himachal-pradesh",
      name: "Himachal Pradesh",
      preview: true,
    },
    {
      id: "jharkhand",
      name: "Jharkhand",
      preview: true,
    },
    {
      id: "nagaland",
      name: "Nagaland",
      preview: true,
    },
    {
      id: "tripura",
      name: "Tripura",
      preview: true,
    },
    {
      id: "chandigarh",
      name: "Chandigarh",
      preview: true,
    },
    {
      id: "assam",
      name: "Assam",
      preview: true,
    },
    {
      id: "sikkim",
      name: "Sikkim",
      preview: true,
    },
    {
      id: "manipur",
      name: "Manipur",
      preview: true,
    },
    {
      id: "chhattisgarh",
      name: "Chattisgarh",
      preview: true,
    },
    { id: "sikkim", name: "Sikkim", preview: true },

    { id: "nagaland", name: "Nagaland", preview: true },

    { id: "tripura", name: "Tripura", preview: true },
  ];
  const servicesData = useSelector((state) =>
    state?.services?.allServices.length > 0
      ? state?.services?.allServices
      : staticServices
  );

  const states = useSelector((state) =>
    state?.documentServiceSlice?.availableStates.length > 0
      ? state?.documentServiceSlice?.availableStates
      : newstates
  );

  useEffect(() => {
    if (!serviceSlug) return;

    const isService = servicesData.some((s) => s.id === serviceSlug);
    if (isService) {
      setIsValidService("services");
      return;
    }

    const isState = states.some((s) => s.id === serviceSlug);
    if (isState) {
      setIsValidService("documents");
      return;
    }

    setIsValidService(false);
  }, [serviceSlug]);

  // The CERSAI Mortgage Report moved from a nationwide property service to a
  // per-state document service. Old links (audit-report PDFs, bookmarks, search
  // results) land on the visitor's last-picked state, defaulting to Telangana.
  if (serviceSlug === "mortgage-report") {
    let stateId = "telangana";
    try {
      const saved = JSON.parse(localStorage.getItem("SelectedState"));
      if (saved?.id && states.some((s) => s.id === saved.id)) stateId = saved.id;
    } catch (e) {
      // no saved state — keep the default
    }
    return <Navigate to={`/${stateId}/mortgage-report`} replace />;
  }

  if (isValidService === null) return <LazyLoading />;

  if (!isValidService)
    return (
      <React.Suspense fallback={<LazyLoading />}>
        <ErrorPage />
      </React.Suspense>
    );

  return (
    <React.Suspense fallback={<LazyLoading />}>
      <AnnouncementBar />
      {/* {
        isValidService !== 'services' &&
      <Navbar />

      } */}

      {isValidService === "services" ? (
        <Routes>
          <Route path="/" element={<ServicesPage />} />
          <Route path="property/*" element={<PropertyRouter />} />
        </Routes>
      ) : (
        <Routes>
          {/* when parent matched `/:serviceSlug`, remaining path is relative
              - index (no further segment) -> show states list for the slug
              - `:document` -> show state document page (e.g. /telangana/loan-pack)
          */}
          <Route path="" element={<StatesDocs key={serviceSlug} />} />

          <Route
            path=":document"
            element={<StateDocuments key={serviceSlug} />}
          />

          <Route path="state/:stateName" element={<State />} />
        </Routes>
      )}

      {isValidService === "documents" ? (
        /* After a state is selected, show a "request a document" nudge above a
           slim footer (bottom bar only, no About/Contact cards). */
        <>
        <div className="w-full bg-bg-white py-12">
          <div className="mx-auto w-full max-w-screen-xl px-4 sm:px-6 lg:px-6">
            <div className="flex w-full flex-col items-center gap-6 rounded-2xl border-2 border-[#d8e2ee] bg-gradient-to-br from-[#f2f6fb] to-[#e4edf8] px-8 py-8 text-center shadow-[0_12px_34px_-12px_rgba(28,75,147,0.45)] sm:flex-row sm:items-center sm:justify-between sm:text-left">
              <div>
                <h3 className="text-xl font-bold text-[#153568]">
                  Can&apos;t find what you&apos;re looking for?
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-500">
                  If your document isn&apos;t listed, let us know. We&apos;ll
                  find it for you or arrange a call back.
                </p>
              </div>
              <Link
                to="/contactus"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-[#153568] px-7 py-3.5 text-sm font-bold text-white shadow-[0_10px_28px_-10px_rgba(13,31,53,0.5)] transition hover:-translate-y-0.5 hover:bg-[#0f2a52]"
              >
                Raise a Request
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
        <Footer showTop={false} />
        </>
      ) : (
        <Footer />
      )}
    </React.Suspense>
  );
};

export default DynamicServiceResolver;
